/**
 * services/messagesApi.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Direct messaging, ratings, and notification methods.
 *
 * METHODS:
 *   Messages:
 *     sendMessage(adId, messageData)          — send a DM about an ad
 *     getAdMessages(adId)                     — get messages for an ad
 *     getMyConversations()                    — get all conversations
 *     getConversationMessages(conversationId) — messages in a thread
 *   Ratings:
 *     submitRating(adId, ratingData)  — rate a seller after a transaction
 *     getAdRatings(adId)              — get all ratings for an ad
 *     reportSeller(adId, reportData)  — report a seller for violations
 *   Notifications:
 *     getNotifications()              — fetch all notifications
 *     markNotificationRead(id)        — mark one notification as read
 *     markAllNotificationsRead()      — mark all notifications as read
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { API_BASE_URL, _isHtmlResponse } from './apiUtils.js';

function _getToken() {
  return (
    localStorage.getItem('authToken')  ||
    localStorage.getItem('token')      ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('admin_token') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('authToken') ||
    sessionStorage.getItem('token')    ||
    sessionStorage.getItem('adminToken')
  );
}

function _adminHeaders(token) {
  const t = token || _getToken();
  return { Authorization: `Token ${t}`, 'Content-Type': 'application/json' };
}

// ── Methods — exported as a plain object, spread into ApiService in api.js ────
export const methods = {
async sendMessage(adId, messageData) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'Not authenticated. Please log in.' };
      const res = await fetch(`${API_BASE_URL}/ads/${adId}/messages/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(messageData),
      });
      if (_isHtmlResponse(res)) {
        return { success: false, error: 'Message endpoint not found on server. Please contact support.' };
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || err.detail || err.message || 'Failed to send message' };
      }
      const data = await res.json();
      return { success: true, message: data.message || 'Message sent successfully!', data };
    } catch (error) { return { success: false, error: error.message || 'Network error. Please try again.' }; }
  },

  async getAdMessages(adId) {
    try {
      const token = this._getToken();
      if (!token) return [];
      const res = await fetch(`${API_BASE_URL}/ads/${adId}/messages/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (!res.ok) throw new Error('Failed to get messages');
      const data = await res.json();
      if (data?.messages) return data.messages;
      return Array.isArray(data) ? data : (data.results || []);
    } catch { return []; }
  },

  async getMyConversations() {
    try {
      const token = this._getToken();
      if (!token) return [];
      const res = await fetch(`${API_BASE_URL}/messages/conversations/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (!res.ok) throw new Error('Failed to get conversations');
      const data = await res.json();
      return Array.isArray(data) ? data : (data.conversations || data.results || []);
    } catch { return []; }
  },

  async getConversationMessages(conversationId) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, messages: [] };
      const res = await fetch(`${API_BASE_URL}/messages/conversations/${conversationId}/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (!res.ok) throw new Error('Failed to get messages');
      const data = await res.json();
      return { success: true, messages: data.messages || [], conversation: data.conversation || null };
    } catch { return { success: false, messages: [] }; }
  },
// RATINGS

  async submitRating(adId, ratingData) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'Please log in to submit a rating' };
      const res = await fetch(`${API_BASE_URL}/ads/${adId}/rate/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(ratingData),
      });
      if (_isHtmlResponse(res)) {
        return { success: false, error: 'Rating endpoint not configured on server. Contact support.' };
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || err.detail || 'Failed to submit rating' };
      }
      const data = await res.json();
      return { success: true, id: data.id, rating: data.rating, message: data.message || 'Rating submitted successfully!', data };
    } catch (error) { return { success: false, error: error.message }; }
  },

  async getAdRatings(adId) {
    try {
      const token = this._getToken();
      const headers = token ? { 'Authorization': `Token ${token}` } : {};
      const res = await fetch(`${API_BASE_URL}/ads/${adId}/rate/`, { headers });
      if (!res.ok) throw new Error('Failed to get ratings');
      const data = await res.json();
      return { success: true, average: data.average || 0, count: data.count || 0, ratings: data.ratings || [] };
    } catch { return { success: false, average: 0, count: 0, ratings: [] }; }
  },
// REPORT SELLER

  async reportSeller(adId, reportData) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'Please log in to report a seller' };
      const res = await fetch(`${API_BASE_URL}/ads/${adId}/report/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(reportData),
      });
      if (_isHtmlResponse(res)) {
        return { success: false, error: 'Report endpoint not configured on server. Contact support.' };
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || err.detail || 'Failed to submit report' };
      }
      const data = await res.json();
      return { success: true, message: data.message || 'Report submitted successfully!' };
    } catch (error) { return { success: false, error: error.message }; }
  }

  // NOTIFICATIONS
};

